import { createContext, useContext, useState, useEffect } from "react";
import {
  addToCartApi,
  updateCartQuantityApi,
  removeFromCartApi,
  getCartApi,
  clearCartApi,
} from "../api/cart.service";

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [promo, setPromo] = useState(null);

  const PROMO_DISCOUNT = 0.1;

  /* =========================
     FETCH CART ON LOAD
  ========================= */
  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const res = await getCartApi();
      const items = res.data?.data?.items || [];

      // Backend structure: items.product
      const formattedCart = items.map((item) => ({
        ...item.product,
        qty: item.quantity,
      }));

      setCart(formattedCart);
    } catch (error) {
      // Keep cart fail-safe silent here; consumers handle empty states.
    }
  };

  /* =========================
     ADD
  ========================= */
  const addToCart = async (product) => {
    try {
      await addToCartApi({
        productId: product._id,
        quantity: 1,
      });
      fetchCart();
    } catch (error) {
      return;
    }
  };

  /* =========================
     DECREASE
  ========================= */
  const decreaseQty = async (id) => {
    const current = cart.find((p) => p._id === id);
    if (!current) return;

    try {
      await updateCartQuantityApi({
        productId: id,
        quantity: current.qty - 1,
      });
      fetchCart();
    } catch (error) {
      return;
    }
  };

  /* =========================
     REMOVE
  ========================= */
  const removeFromCart = async (id) => {
    try {
      await removeFromCartApi(id);
      fetchCart();
    } catch (error) {
      return;
    }
  };

  /* CLEAR */
  const clearCart = async () => {
    try {
      await clearCartApi();
      setCart([]);
    } catch (error) {
      return;
    }
  };

  /* COUNT */
  const totalItems = cart.reduce(
    (sum, item) => sum + item.qty,
    0
  );

  /* PRICE */
  const subtotal = cart.reduce(
    (sum, item) =>
      sum + (item.discountPrice || item.price) * item.qty,
    0
  );

  const discount = promo
    ? Math.round(subtotal * PROMO_DISCOUNT)
    : 0;

  const total = subtotal - discount;

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        decreaseQty,
        removeFromCart,
        clearCart,
        promo,
        setPromo,
        subtotal,
        discount,
        total,
        totalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);